import { useState, useCallback, useRef, useEffect } from "react";

export interface DragTable {
  id: string;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
}

interface DragState {
  tableId: string;
  startMouseX: number;
  startMouseY: number;
  startTableX: number;
  startTableY: number;
}

interface AlignmentGuide {
  type: "vertical" | "horizontal";
  position: number; // percentage
}

interface UseTableDragOptions {
  tables: DragTable[];
  gridSize?: number;
  snapToGrid?: boolean;
  snapThreshold?: number; // px for smart guides
  editable?: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onTableMove?: (tableId: string, x: number, y: number) => void;
}

export function useTableDrag({
  tables,
  gridSize = 16,
  snapToGrid = true,
  snapThreshold = 5,
  editable = true,
  containerRef,
  onTableMove,
}: UseTableDragOptions) {
  const [draggingTableId, setDraggingTableId] = useState<string | null>(null);
  const [dragPositions, setDragPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const [guides, setGuides] = useState<AlignmentGuide[]>([]);
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);
  const dragStateRef = useRef<DragState | null>(null);

  const getTablePosition = useCallback(
    (table: DragTable) => {
      const override = dragPositions[table.id];
      return override ?? { x: table.positionX, y: table.positionY };
    },
    [dragPositions],
  );

  // Smart guides: find alignment with other tables
  const findGuides = useCallback(
    (
      movingId: string,
      newX: number,
      newY: number,
      movingTable: DragTable,
    ): { guides: AlignmentGuide[]; snapX: number; snapY: number } => {
      const foundGuides: AlignmentGuide[] = [];
      let snapX = newX;
      let snapY = newY;
      const cellPct = 100 / gridSize;
      const thresholdCells =
        snapThreshold /
        ((containerRef.current?.getBoundingClientRect().width ?? 600) /
          gridSize);

      for (const t of tables) {
        if (t.id === movingId) continue;
        const pos = getTablePosition(t);

        // Left edge alignment
        if (Math.abs(newX - pos.x) < thresholdCells) {
          snapX = pos.x;
          foundGuides.push({ type: "vertical", position: pos.x * cellPct });
        }
        // Right edge alignment
        if (
          Math.abs(newX + movingTable.width - (pos.x + t.width)) <
          thresholdCells
        ) {
          snapX = pos.x + t.width - movingTable.width;
          foundGuides.push({
            type: "vertical",
            position: (pos.x + t.width) * cellPct,
          });
        }
        // Center X alignment
        const myCenterX = newX + movingTable.width / 2;
        const otherCenterX = pos.x + t.width / 2;
        if (Math.abs(myCenterX - otherCenterX) < thresholdCells) {
          snapX = otherCenterX - movingTable.width / 2;
          foundGuides.push({
            type: "vertical",
            position: otherCenterX * cellPct,
          });
        }

        // Top edge alignment
        if (Math.abs(newY - pos.y) < thresholdCells) {
          snapY = pos.y;
          foundGuides.push({ type: "horizontal", position: pos.y * cellPct });
        }
        // Bottom edge alignment
        if (
          Math.abs(newY + movingTable.height - (pos.y + t.height)) <
          thresholdCells
        ) {
          snapY = pos.y + t.height - movingTable.height;
          foundGuides.push({
            type: "horizontal",
            position: (pos.y + t.height) * cellPct,
          });
        }
        // Center Y alignment
        const myCenterY = newY + movingTable.height / 2;
        const otherCenterY = pos.y + t.height / 2;
        if (Math.abs(myCenterY - otherCenterY) < thresholdCells) {
          snapY = otherCenterY - movingTable.height / 2;
          foundGuides.push({
            type: "horizontal",
            position: otherCenterY * cellPct,
          });
        }
      }

      return { guides: foundGuides, snapX, snapY };
    },
    [tables, gridSize, snapThreshold, containerRef, getTablePosition],
  );

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent, table: DragTable) => {
      if (!editable) return;
      e.preventDefault();
      e.stopPropagation();
      const pos = getTablePosition(table);
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      dragStateRef.current = {
        tableId: table.id,
        startMouseX: clientX,
        startMouseY: clientY,
        startTableX: pos.x,
        startTableY: pos.y,
      };
      setDraggingTableId(table.id);
    },
    [editable, getTablePosition],
  );

  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const state = dragStateRef.current;
      if (!state || !containerRef.current) return;
      e.preventDefault();

      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      const rect = containerRef.current.getBoundingClientRect();
      const cellPx = rect.width / gridSize;

      const dx = (clientX - state.startMouseX) / cellPx;
      const dy = (clientY - state.startMouseY) / cellPx;

      const movingTable = tables.find((t) => t.id === state.tableId);
      if (!movingTable) return;

      let newX = state.startTableX + dx;
      let newY = state.startTableY + dy;

      // Clamp to grid bounds
      newX = Math.max(0, Math.min(gridSize - movingTable.width, newX));
      newY = Math.max(0, Math.min(gridSize - movingTable.height, newY));

      if (snapToGrid) {
        // Snap to nearest grid cell
        newX = Math.round(newX);
        newY = Math.round(newY);
      }

      // Smart guides
      const {
        guides: newGuides,
        snapX,
        snapY,
      } = findGuides(state.tableId, newX, newY, movingTable);
      setGuides(newGuides);

      // Apply smart snap if not grid snapping
      if (!snapToGrid) {
        newX = snapX;
        newY = snapY;
      }

      setDragPositions((prev) => ({
        ...prev,
        [state.tableId]: { x: newX, y: newY },
      }));
    },
    [containerRef, gridSize, snapToGrid, tables, findGuides],
  );

  const handleDragEnd = useCallback(() => {
    const state = dragStateRef.current;
    if (state) {
      const pos = dragPositions[state.tableId];
      if (pos && onTableMove) {
        onTableMove(state.tableId, pos.x, pos.y);
      }
    }
    setDraggingTableId(null);
    setGuides([]);
    dragStateRef.current = null;
  }, [dragPositions, onTableMove]);

  // Global event listeners
  useEffect(() => {
    if (!draggingTableId) return;
    const moveHandler = (e: MouseEvent | TouchEvent) => handleDragMove(e);
    const endHandler = () => handleDragEnd();
    window.addEventListener("mousemove", moveHandler);
    window.addEventListener("mouseup", endHandler);
    window.addEventListener("touchmove", moveHandler, { passive: false });
    window.addEventListener("touchend", endHandler);
    return () => {
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("mouseup", endHandler);
      window.removeEventListener("touchmove", moveHandler);
      window.removeEventListener("touchend", endHandler);
    };
  }, [draggingTableId, handleDragMove, handleDragEnd]);

  // Selection
  const toggleSelect = useCallback((tableId: string, multi: boolean) => {
    setSelectedTableIds((prev) => {
      if (multi) {
        return prev.includes(tableId)
          ? prev.filter((id) => id !== tableId)
          : [...prev, tableId];
      }
      return prev.includes(tableId) && prev.length === 1 ? [] : [tableId];
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedTableIds([]), []);

  // Alignment helpers
  const alignTables = useCallback(
    (
      direction:
        | "left"
        | "right"
        | "top"
        | "bottom"
        | "centerH"
        | "centerV"
        | "distributeH"
        | "distributeV",
    ) => {
      if (selectedTableIds.length < 2) return;

      const selected = tables.filter((t) => selectedTableIds.includes(t.id));
      const positions = selected.map((t) => ({
        ...t,
        ...getTablePosition(t),
      }));

      const newPositions: Record<string, { x: number; y: number }> = {};

      switch (direction) {
        case "left": {
          const minX = Math.min(...positions.map((p) => p.x));
          positions.forEach((p) => {
            newPositions[p.id] = { x: minX, y: p.y };
          });
          break;
        }
        case "right": {
          const maxRight = Math.max(...positions.map((p) => p.x + p.width));
          positions.forEach((p) => {
            newPositions[p.id] = { x: maxRight - p.width, y: p.y };
          });
          break;
        }
        case "top": {
          const minY = Math.min(...positions.map((p) => p.y));
          positions.forEach((p) => {
            newPositions[p.id] = { x: p.x, y: minY };
          });
          break;
        }
        case "bottom": {
          const maxBottom = Math.max(...positions.map((p) => p.y + p.height));
          positions.forEach((p) => {
            newPositions[p.id] = { x: p.x, y: maxBottom - p.height };
          });
          break;
        }
        case "centerH": {
          const centers = positions.map((p) => p.x + p.width / 2);
          const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
          positions.forEach((p) => {
            newPositions[p.id] = {
              x: Math.round(avgCenter - p.width / 2),
              y: p.y,
            };
          });
          break;
        }
        case "centerV": {
          const centers = positions.map((p) => p.y + p.height / 2);
          const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
          positions.forEach((p) => {
            newPositions[p.id] = {
              x: p.x,
              y: Math.round(avgCenter - p.height / 2),
            };
          });
          break;
        }
        case "distributeH": {
          const sorted = [...positions].sort((a, b) => a.x - b.x);
          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const totalWidth = sorted.reduce((sum, p) => sum + p.width, 0);
          const totalSpace = last.x + last.width - first.x - totalWidth;
          const gap = totalSpace / (sorted.length - 1);
          let currentX = first.x;
          sorted.forEach((p) => {
            newPositions[p.id] = { x: Math.round(currentX), y: p.y };
            currentX += p.width + gap;
          });
          break;
        }
        case "distributeV": {
          const sorted = [...positions].sort((a, b) => a.y - b.y);
          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const totalHeight = sorted.reduce((sum, p) => sum + p.height, 0);
          const totalSpace = last.y + last.height - first.y - totalHeight;
          const gap = totalSpace / (sorted.length - 1);
          let currentY = first.y;
          sorted.forEach((p) => {
            newPositions[p.id] = { x: p.x, y: Math.round(currentY) };
            currentY += p.height + gap;
          });
          break;
        }
      }

      // Apply new positions
      setDragPositions((prev) => ({ ...prev, ...newPositions }));
      // Notify parent
      Object.entries(newPositions).forEach(([id, pos]) => {
        onTableMove?.(id, pos.x, pos.y);
      });
    },
    [selectedTableIds, tables, getTablePosition, onTableMove],
  );

  return {
    draggingTableId,
    dragPositions,
    guides,
    selectedTableIds,
    getTablePosition,
    handleDragStart,
    toggleSelect,
    clearSelection,
    alignTables,
    setDragPositions,
  };
}
